import {Notification, Transition} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import {
  MdCheck,
  MdOutlineAddCircleOutline,
  MdOutlineCheck,
  MdOutlinePlaylistAdd,
  MdOutlinePlaylistAddCheck,
} from 'react-icons/md';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {cn} from '../../../lib/classname';
import {showErrorSnackBar} from '../../../shared/components';
import {NoLoginAlertModal} from '../../../shared/components/NoLoginAlertModal';
import {useHandleCalendar} from '../usecases/useHandleCalendar';

type ButtonStyle = 'text' | 'icon' | 'textWithBorder' | 'primary';

export type RecruitmentDataForSchedule = {
  companyId: number;
  companyName: string;
  detailURL: string;
};

type Props = {
  scheduleId: number;
  isRegisteredInitial: boolean;
  registeredCountInitial: number;
  buttonStyle?: ButtonStyle;
  recruitmentData?: RecruitmentDataForSchedule & {entryTitle: string};
  dataGtmClick?: string;
  dataGtmContent?: string;
};

export const ScheduleButton: React.FC<Props> = ({
  scheduleId,
  isRegisteredInitial,
  registeredCountInitial,
  buttonStyle = 'text',
  dataGtmClick,
  dataGtmContent,
}: Props) => {
  const {status} = useAuth();
  const [opened, {open, close}] = useDisclosure(false);
  const {isRegistered, handleCalendar} = useHandleCalendar({
    scheduleId,
    isRegisteredInitial,
    registeredCountInitial,
  });

  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const [errorOccurred, setErrorOccurred] = useState(false);

  const handleButtonClick = async () => {
    if (status !== 'authenticated') {
      open();
      return;
    }

    try {
      setButtonDisabled(true);
      await handleCalendar();
      setButtonDisabled(false);
    } catch {
      setErrorOccurred(true);
      showErrorSnackBar({
        isNetworkError: false,
        title: '更新処理に失敗しました',
        message:
          '通信状況をご確認ください。Googleカレンダー連携を設定されている場合は、一度解除していただき再連携をお願いいたします。',
      });
    }
  };

  return (
    <>
      <button
        className={`border-collapse border-[1px] border-primary-1 rounded-4 ${errorOccurred ? 'text-red-600' : 'text-primary-1'} ${isRegistered ? 'bg-primary-1 text-white' : 'bg-white text-primary-1'} py-[3px] relative ${buttonStyle === 'icon' ? 'w-[73px] h-[26px] pt-[5px]' : 'w-[90px]'} flex justify-center items-center hover:opacity-80`}
        disabled={isButtonDisabled || errorOccurred}
        onClick={handleButtonClick}
        type="button"
        data-gtm-click={dataGtmClick}
        data-gtm-content={dataGtmContent}
      >
        <sup
          className={`text-base absolute ${buttonStyle === 'text' ? 'top-[3px]' : 'top-[5px]'} left-4`}
        >
          +
        </sup>
        <span className="text-xs leading">
          {errorOccurred
            ? '更新エラー'
            : isButtonDisabled
              ? '更新中'
              : isRegistered
                ? '登録済み'
                : '予定を追加'}
        </span>
      </button>
      <NoLoginAlertModal opened={opened} onClose={close} />
    </>
  );
};

// 新デザインのスケジュールボタン
// 使われている場所のデザイン変更完了後こちらに統合する
export const ScheduleButtonNewDesign: React.FC<Props> = ({
  scheduleId,
  isRegisteredInitial,
  registeredCountInitial,
  recruitmentData,
  buttonStyle = 'text',
  dataGtmClick,
  dataGtmContent,
}: Props) => {
  const {status} = useAuth();
  const [opened, {open, close}] = useDisclosure(false);
  const {isRegistered, handleCalendar, notificationOpened} = useHandleCalendar({
    scheduleId,
    isRegisteredInitial,
    registeredCountInitial,
    recruitmentData,
  });

  const baseClasses = {
    default: 'relative flex justify-center items-center hover:opacity-50',
  } satisfies Record<'default', string>;

  const registeredClasses = {
    registered: 'text-success-2',
    notRegistered: 'text-black-2',
  } satisfies Record<'registered' | 'notRegistered', string>;

  const sizeClasses = {
    text: 'text-base',
    icon: 'w-[90px]',
    textWithBorder: 'text-sm w-full',
    primary: 'w-full h-[50px] rounded-4 shadow-md',
  } satisfies Record<ButtonStyle, string>;

  const handleButtonClick = async () => {
    if (status !== 'authenticated') {
      open();
      return;
    }

    try {
      await handleCalendar();
    } catch {
      showErrorSnackBar({
        isNetworkError: false,
        title: '更新処理に失敗しました',
        message:
          '通信状況をご確認ください。Googleカレンダー連携を設定されている場合は、一度解除していただき再連携をお願いいたします。',
      });
    }
  };

  const isPrimaryDisabled = buttonStyle === 'primary' && isRegistered;

  return (
    <div className="relative">
      <button
        className={cn(
          baseClasses.default,
          buttonStyle !== 'primary' &&
            (isRegistered
              ? registeredClasses.registered
              : registeredClasses.notRegistered),
          buttonStyle === 'primary' &&
            (isRegistered
              ? 'bg-black-3 text-white cursor-not-allowed hover:opacity-100'
              : 'bg-primary text-white'),
          sizeClasses[buttonStyle],
        )}
        onClick={handleButtonClick}
        disabled={isPrimaryDisabled}
        type="button"
        data-gtm-click={dataGtmClick}
        data-gtm-content={dataGtmContent}
      >
        {buttonStyle === 'icon' &&
          (isRegistered ? (
            <div className="border border-success-2 bg-success-2 text-white rounded-full p-4">
              <MdOutlinePlaylistAddCheck size={28} />
            </div>
          ) : (
            <div className="border border-black-2 rounded-full p-4">
              <MdOutlinePlaylistAdd size={28} />
            </div>
          ))}
        {buttonStyle === 'text' && (
          <span className="text-base font-bold flex items-center gap-[2px]">
            {isRegistered ? (
              <>
                <MdOutlinePlaylistAddCheck size={20} />
                追加済み
              </>
            ) : (
              <>
                <MdOutlinePlaylistAdd size={20} />
                予定を追加
              </>
            )}
          </span>
        )}
        {buttonStyle === 'primary' && (
          <span className="flex items-center justify-center gap-8 font-bold text-base">
            {isRegistered ? (
              <>
                <MdOutlinePlaylistAddCheck size={22} />
                追加済み
              </>
            ) : (
              <>
                <MdOutlinePlaylistAdd size={22} />
                予定を追加
              </>
            )}
          </span>
        )}
        {buttonStyle === 'textWithBorder' &&
          (isRegistered ? (
            <div className="flex items-center gap-4 border border-primary bg-primary text-white rounded-full text-nowrap px-8 py-4">
              <MdOutlineCheck size={12} />
              追加済み
            </div>
          ) : (
            <div className="flex items-center gap-4 border border-primary text-primary rounded-full text-nowrap px-8 py-4">
              <MdOutlineAddCircleOutline size={12} />
              予定を追加
            </div>
          ))}
      </button>
      {buttonStyle !== 'primary' && (
        <div className="absolute pc:top-24 top-36 right-0 w-[260px] mt-2">
          <Transition
            mounted={notificationOpened}
            transition="fade"
            duration={500}
          >
            {(styles) => (
              <Notification
                style={styles}
                icon={<MdCheck />}
                color="teal"
                radius="md"
                title="カレンダーに追加しました！"
                withCloseButton={false}
              />
            )}
          </Transition>
        </div>
      )}
      <NoLoginAlertModal opened={opened} onClose={close} />
    </div>
  );
};
